import React, { useMemo, useState } from 'react';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import CustomCard from '../../Shared/Components/CustomCard/CustomCard';
import { useSupplierPrepaymentLedgerPage } from '../Hooks/useSupplierPrepaymentLedgerPage';

const formatAmount = (amount: number, currency: string): string =>
  `${amount.toFixed(2)} ${currency}`;

const formatDateTime = (value: string): string => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleString('pl-PL');
};

const formatMovementType = (value: string): string => {
  const normalized = value.trim().toLowerCase();

  switch (normalized) {
    case 'deposit':
      return 'Wpłata';
    case 'allocation':
      return 'Rozliczenie';
    case 'release':
      return 'Odblokowanie';
    case 'refund':
      return 'Zwrot';
    case 'manualadjustment':
    case 'manual_adjustment':
    case 'manual-adjustment':
      return 'Korekta ręczna';
    default:
      return value;
  }
};

const SupplierPrepaymentLedgerPage: React.FC = () => {
  const [openBalanceOnly, setOpenBalanceOnly] = useState(false);

  const {
    apContractors,
    selectedApContractorId,
    setApContractorId,
    currency,
    setSelectedCurrency,
    pageNumber,
    setPageNumber,
    totalPages,
    ledger,
    movements,
    totalCount,
    isLoading,
    isFetching,
    errorMessage,
    refetch,
  } = useSupplierPrepaymentLedgerPage();

  const canShowTimeline = useMemo(() => {
    if (!openBalanceOnly) {
      return true;
    }

    return (ledger?.openBalance ?? 0) > 0;
  }, [ledger?.openBalance, openBalanceOnly]);

  return (
    <Stack spacing={3}>
      <CustomCard
        icon={<AccountBalanceWalletIcon color="primary" fontSize="large" />}
        title="Prepayment dostawców"
        subheader="Saldo otwarte i historia ruchów"
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 280 }}>
            <InputLabel id="supplier-prepayment-ap-contractor">AP kontrahent</InputLabel>
            <Select
              labelId="supplier-prepayment-ap-contractor"
              label="AP kontrahent"
              value={selectedApContractorId ?? ''}
              onChange={(event) => {
                const value = event.target.value;
                setApContractorId(value ? Number(value) : undefined);
              }}
            >
              <MenuItem value="">Wybierz AP kontrahenta</MenuItem>
              {apContractors.map((apContractor) => (
                <MenuItem key={apContractor.id} value={apContractor.id}>
                  {apContractor.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Waluta"
            size="small"
            value={currency}
            onChange={(event) => setSelectedCurrency(event.target.value)}
            inputProps={{ maxLength: 3 }}
            sx={{ width: 140 }}
          />

          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
            disabled={isFetching || !selectedApContractorId}
          >
            Odśwież
          </Button>

          <Button
            variant={openBalanceOnly ? 'contained' : 'outlined'}
            onClick={() => setOpenBalanceOnly((current) => !current)}
            disabled={!selectedApContractorId}
          >
            Preset: Otwarte salda
          </Button>
        </Stack>

        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!isLoading && ledger && (
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} sx={{ mb: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Saldo otwarte
              </Typography>
              <Typography variant="h6">
                {formatAmount(ledger.openBalance, ledger.currency)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Liczba ruchów
              </Typography>
              <Typography variant="h6">{ledger.totalMovements}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Ostatnia aktualizacja
              </Typography>
              <Typography variant="h6">{formatDateTime(ledger.updatedAtUtc)}</Typography>
            </Box>
          </Stack>
        )}
      </CustomCard>

      <CustomCard title="Timeline ruchów" icon={<AccountBalanceWalletIcon color="primary" />}>
        {!isLoading && canShowTimeline && movements.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Brak ruchów dla wybranego filtra.
          </Typography>
        )}

        {!isLoading && !canShowTimeline && (
          <Alert severity="info">Preset „Otwarte salda” ukrył firmę bez otwartego salda.</Alert>
        )}

        {!isLoading && canShowTimeline && movements.length > 0 && (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell>Typ</TableCell>
                  <TableCell>Kwota</TableCell>
                  <TableCell>Powiązania</TableCell>
                  <TableCell>Opis</TableCell>
                  <TableCell>Użytkownik</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {movements.map((movement) => (
                  <TableRow key={movement.movementId}>
                    <TableCell>{formatDateTime(movement.createdAtUtc)}</TableCell>
                    <TableCell>{formatMovementType(movement.type)}</TableCell>
                    <TableCell>{formatAmount(movement.amount, currency)}</TableCell>
                    <TableCell>
                      <Typography variant="caption" display="block">
                        FV kosztowa: {movement.expenseInvoiceId ?? '-'}
                      </Typography>
                      <Typography variant="caption" display="block">
                        Operacja bankowa: {movement.bankDocumentEntryId ?? '-'}
                      </Typography>
                      <Typography variant="caption" display="block">
                        Rezerwacja: {movement.bookingId ?? '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>{movement.description ?? '-'}</TableCell>
                    <TableCell>{movement.createdByUserId}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
              spacing={2}
              sx={{ mt: 2 }}
            >
              <Typography variant="body2" color="text.secondary">
                Łącznie ruchów: {totalCount}
              </Typography>
              <Pagination
                page={pageNumber}
                count={totalPages}
                onChange={(_event, value) => setPageNumber(value)}
                color="primary"
                disabled={isFetching || totalPages <= 1}
              />
            </Stack>
          </>
        )}
      </CustomCard>
    </Stack>
  );
};

export default SupplierPrepaymentLedgerPage;
